import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { createSharedComposable } from '@vueuse/core'
import { type ProfileMenuKey } from '@klausapp/app-navigation/types'
import i18n from '@/i18n'
import { session } from '@/composables/useSession'
import { useAccountList } from '@/composables/useAccountList'
import { hasZendeskProxy } from '@/config'
import { hasAccountRole, hasHighestWorkspaceRole } from '@/utils/roleUtils'

import { type Tippy } from '@/components/Tippy'
import analytics from '@/utils/analytics'
import timeTracking from '@/modules/time-tracking/timeTracking'
import { logout } from '@/modules/auth/api'

export default createSharedComposable(() => {
  const router = useRouter()
  const route = useRoute()

  const tippyRef = ref<InstanceType<typeof Tippy>>()
  const isOpen = ref(false)
  const showAccountSwitchModal = ref(false)

  const [accounts] = useAccountList()

  const profileMenuItems = computed(() => {
    return {
      tooltip: i18n.t('modules.application.navbar.settings'),
      profileMenuProps: {
        name: session.user.name,
        meta: session.user.email,
        avatarUrl: session.user.avatar,
      },
      links: [
        {
          label: i18n.t('modules.application.navbar.settings_submenu.settings'),
          value: 'settings',
          isSelected: route.name === 'profile.general',
        },
        ...(hasAccountRole('ADMIN', 'MANAGER') || hasHighestWorkspaceRole('MANAGER', 'LEAD')
          ? [
              {
                label: session.features.botQa
                  ? i18n.t('modules.application.navbar.settings_submenu.users_bots_workspaces')
                  : i18n.t('modules.application.navbar.settings_submenu.users'),
                value: 'users-bots-workspaces',
                isSelected: route.name === 'settings.users',
              },
            ]
          : []),
      ],
      items: [
        ...(accounts.value.length > 1 && !hasZendeskProxy
          ? [
              {
                label: i18n.t('modules.application.navbar.switch_account'),
                value: 'switch-account',
              },
            ]
          : []),
        {
          label: i18n.t('modules.application.navbar.log_out'),
          value: 'signout',
        },
      ],
    }
  })

  const closeTippy = () => {
    tippyRef.value?.hide()
    isOpen.value = false
  }

  const onLinkClick = (event: string) => {
    analytics.navbarEvent('Clicked navbar popup item', event)
    closeTippy()
  }

  const handleLogout = async () => {
    onLinkClick('Log out')
    await timeTracking.pause()
    logout()
  }

  const openAccountModal = () => {
    showAccountSwitchModal.value = true
    onLinkClick('Switch account')
  }

  const onProfileMenuItemClick = (key: ProfileMenuKey) => {
    switch (key) {
      case 'profile':
        analytics.navbarEvent('Clicked navbar link', 'Avatar')
        break
      case 'settings':
        router.push({ name: 'profile.general' })
        onLinkClick('Settings')
        break
      case 'users-bots-workspaces':
        router.push(hasHighestWorkspaceRole('LEAD') ? { name: 'settings.groups' } : { name: 'settings.users' })
        onLinkClick('Users & Workspaces')
        break
      case 'switch-account':
        openAccountModal()
        break
      case 'signout':
        handleLogout()
        break
      default:
        break
    }
  }

  return {
    tippyRef,
    isOpen,
    showAccountSwitchModal,
    openAccountModal,
    onLinkClick,
    handleLogout,
    profileMenuItems,
    onProfileMenuItemClick,
  }
})
